import React from 'react';
import { useTranslation } from 'react-i18next';
import { TDR } from 'tdr-common';
import { resolveLocalizedText } from '../../../helpers/resolveLocalizedText';
import { SearchFilters } from '../../../routes/AvailabilityGuard';
import { AnnouncementResponse } from '../../announcements/common';
import { AvailableCard } from '../../inventory-carousel/AvailableCard';
import { CarouselSkeleton } from '../../inventory-carousel/CarouselSkeleton';
import { CrossSellDateCard } from '../../inventory-carousel/CrossSellDateCard';
import { CrossSellGroupSizeCard } from '../../inventory-carousel/CrossSellGroupSizeCard';
import { CrossSellTimeCard } from '../../inventory-carousel/CrossSellTimeCard';
import { getSearchResult, TableCategoryCounts } from '../../inventory-carousel/categorizeTables';
import {
	InventoryEmptyState,
	InventorySectionCategories,
	resolveInventorySection
} from './inventoryTables';

// Re-exported so existing importers keep their path. Deliberately types only: a value
// re-export would put the resolver back inside a module that tests stub out.
export type { InventorySectionCategories, ResolvedInventorySection } from './inventoryTables';

export type InventorySectionProps = {
	section: TDR.SearchConfig.InventorySection;
	restaurant: TDR.Restaurant;
	searchFilters: SearchFilters;
	categories: InventorySectionCategories;
	counts: TableCategoryCounts;
	totalResults: number;
	results?: TDR.Availability.searchOutput;
	announcements?: Record<string, AnnouncementResponse>;
	viewedTableId?: string;
	timeslotDocMap?: Record<string, { timeSlots?: TDR.TimeSlotMap }>;
	hasScrolledToBottom: boolean;
	showSkeleton: boolean;
	emptyState: InventoryEmptyState;
	/** `slotTime` is set when the section is service-scoped, so navigation can open that package. */
	onTableClick: (table: TDR.Table, index: number, slotTime?: string) => void;
	cardContainerRef?: React.Ref<HTMLDivElement>;
	/**
	 * Where this section's cards start in the page-wide ordering reported to analytics.
	 * A config may render several inventory sections; without an offset each would restart
	 * its `position` at 0 and the click events would collide. See ResultsSections.
	 */
	positionOffset?: number;
};

export function InventorySection(props: InventorySectionProps) {
	const { t, i18n } = useTranslation();
	const {
		section, restaurant, searchFilters, categories, counts, totalResults, results,
		announcements, viewedTableId, timeslotDocMap, hasScrolledToBottom,
		showSkeleton, emptyState, onTableClick, cardContainerRef, positionOffset = 0
	} = props;

	// Via getSearchResult, not bracket access: resultMap is typed as a Map but arrives as a plain
	// object through JSON. This now decides section MEMBERSHIP for x-sell-time, not just display,
	// so guessing the shape wrong would silently drop tables rather than merely blank a card.
	const availableTimesFor = (tableId: string) => getSearchResult(results?.resultMap, tableId)?.availableTimes;

	const { order, tablesFor, emptyStateTables, slotTimeFor, crossSellTimesFor } = resolveInventorySection(
		section,
		{ categories, searchFilters, emptyState, timeslotDocMap, availableTimesFor }
	);

	const allEmpty = order.every(category => tablesFor(category).length === 0);

	// A service scope is resolved from the searched date's slots, which arrive on their own query —
	// separate from the search that drives showSkeleton. In the window between the two, and forever
	// if the slots request fails, this section would otherwise report "no tables" when the honest
	// answer is "not known yet", and vanish with no explanation. Skeleton instead.
	const awaitingSlots = !!section.services?.length && !!searchFilters.date && !timeslotDocMap;

	const noCardsWillRender =
		emptyState.show
			? !emptyState.isLoading && emptyStateTables.length === 0
			: !showSkeleton && !awaitingSlots && allEmpty;

	if (noCardsWillRender) {
		return null;
	}

	// A configured header that resolves to nothing (e.g. copy exists only for another
	// locale) falls back to the default rather than leaving the section unlabelled.
	const configuredHeader = section.header
		? resolveLocalizedText(section.header, i18n.language).trim()
		: '';
	const header = configuredHeader || t('inventory.premium_seating');

	const sharedCardProps = (table: TDR.Table) => ({
		restaurant,
		table,
		searchFilters,
		announcement: announcements?.[table.id],
		viewedTable: table.id === viewedTableId,
		timeslotData: timeslotDocMap?.[table.id]?.timeSlots
	});

	// Resolved for this search's date and groupSize by the availability service; undefined on the
	// early-exit searches (no date, or no groupSize), which is exactly the open-parameters empty
	// state below. AvailableCard treats undefined as "not known yet" rather than "instant".
	const bookingTypeFor = (table: TDR.Table) => getSearchResult(results?.resultMap, table.id)?.bookingType;

	// Only service-scoped sections carry a slot time; an unscoped section calls the handler with
	// exactly the arguments it always did, so nothing downstream has to reason about `undefined`.
	const clickTable = (table: TDR.Table, index: number) => {
		const slotTime = slotTimeFor(table.id);
		return slotTime ? onTableClick(table, index, slotTime) : onTableClick(table, index);
	};

	let position = positionOffset;
	const renderCategory = (category: TDR.SearchConfig.InventoryCategory) =>
		tablesFor(category).map((table) => {
			const index = position++;
			switch (category) {
				case 'available':
					return (
						<AvailableCard
							key={table.id}
							{...sharedCardProps(table)}
							bookingType={bookingTypeFor(table)}
							slotTime={slotTimeFor(table.id)}
							onClick={() => clickTable(table, index)}
						/>
					);
				case 'x-sell-time':
					return (
						<CrossSellTimeCard
							key={table.id}
							{...sharedCardProps(table)}
							availableTimes={crossSellTimesFor(table.id, availableTimesFor(table.id))}
							slotTime={slotTimeFor(table.id)}
							counts={counts}
							totalResults={totalResults}
							position={index}
							hasScrolledToBottom={hasScrolledToBottom}
						/>
					);
				case 'sold-out':
				case 'x-sell-dates':
					return (
						<CrossSellDateCard
							key={table.id}
							{...sharedCardProps(table)}
							counts={counts}
							totalResults={totalResults}
							position={index}
							hasScrolledToBottom={hasScrolledToBottom}
							soldOut={category === 'sold-out'}
						/>
					);
				case 'x-sell-groupsize':
					return (
						<CrossSellGroupSizeCard
							key={table.id}
							{...sharedCardProps(table)}
							counts={counts}
							totalResults={totalResults}
							position={index}
							hasScrolledToBottom={hasScrolledToBottom}
						/>
					);
				default:
					return null;
			}
		});

	return (
		<section className='px-5 pt-4 sm:px-0 sm:pt-6'>
			<h2 className='font-poppins text-tablz-text-gray-2 text-lg font-medium mb-3'>
				{header}
			</h2>
			<div
				className='list-none m-0 grid grid-cols-1 xl:grid-cols-2 gap-4 py-6 place-items-center w-full sm:py-0 h-fit'
				ref={cardContainerRef}
			>
				{emptyState.show ? (
					emptyState.isLoading ? (
						<CarouselSkeleton />
					) : (
						emptyStateTables.map((table, index) => (
							<AvailableCard
								key={table.id}
								{...sharedCardProps(table)}
								bookingType={bookingTypeFor(table)}
								slotTime={slotTimeFor(table.id)}
								onClick={() => clickTable(table, positionOffset + index)}
							/>
						))
					)
				) : showSkeleton || awaitingSlots ? (
					<CarouselSkeleton />
				) : (
					order.map(category => (
						<React.Fragment key={category}>{renderCategory(category)}</React.Fragment>
					))
				)}
			</div>
		</section>
	);
}
